import * as THREE from "three";
import { EventBus } from "../EventBus.js";
import { WorldStore } from "../../store/WorldStore.js";
import type { Position } from "../types/grid.js";
import type {
  StairsDownMetadata,
  StairsUpMetadata,
  TeleporterMetadata,
  TrapMetadata,
} from "../types/tiled.js";
import { INTERACTIVE_LAYER } from "./Raycaster.js";
import { getFloorDecalTexture, type FloorDecalKind } from "./floorDecalTextures.js";

/**
 * Floor-decal rendering (W18).
 *
 * Places a floor-aligned `PlaneGeometry` for each registered world object
 * and swaps its texture in response to `world:trap_*`, `world:stairs_used`,
 * and `world:teleporter_used` events.
 *
 * Decal kinds in v1:
 *   - traps        — invisible until `world:trap_revealed`; armed/disarmed/triggered states.
 *                    Revealed trap meshes join the interactive raycast layer
 *                    (`userData.kind = "trap"`) so the disarm click rule can hit them.
 *   - camps        — always visible campfire marker on authored `camp` cells
 *   - teleporters  — single state; rendered alongside their interact trigger
 *   - stairs_up/down — single directional chevron
 *
 * Item drops are tracked in the spec but deferred to a later slice
 * (no creation flow yet outside enemy loot, which uses its own pile renderer).
 */

const CELL_SIZE = 1;
const DECAL_Y = 0.02; // floats above the floor to avoid z-fighting
const DECAL_SIZE = 0.9; // slightly inset from the cell so wall corners stay clean

export type FloorDecalDescriptor =
  | {
      readonly kind: "trap";
      readonly id: string;
      readonly cell: Position;
      readonly meta: TrapMetadata;
    }
  | {
      readonly kind: "camp";
      readonly id: string;
      readonly cell: Position;
    }
  | {
      readonly kind: "teleporter";
      readonly id: string;
      readonly cell: Position;
      readonly meta: TeleporterMetadata;
    }
  | {
      readonly kind: "stairs";
      readonly id: string;
      readonly cell: Position;
      readonly direction: "up" | "down";
      readonly meta: StairsUpMetadata | StairsDownMetadata;
    };

export class FloorDecalManager {
  private readonly meshes = new Map<string, THREE.Mesh>();
  private readonly traps = new Map<string, FloorDecalDescriptor & { kind: "trap" }>();
  private readonly unsubs: Array<() => void> = [];

  constructor(
    private readonly scene: THREE.Scene,
    decals: readonly FloorDecalDescriptor[],
  ) {
    for (const decal of decals) {
      if (decal.kind === "trap") {
        this.traps.set(decal.id, decal);
        // Render only if already revealed (e.g. restored from a save).
        if (WorldStore.isTrapRevealed(decal.id)) {
          const state: FloorDecalKind = WorldStore.isTrapDisarmed(decal.id)
            ? "trap-disarmed"
            : "trap-armed";
          this.spawn(decalKey(decal), decal.cell, state);
          this.makeTrapPickable(decalKey(decal), decal.id, decal.cell);
        }
      } else if (decal.kind === "camp") {
        this.spawn(decalKey(decal), decal.cell, "camp");
      } else if (decal.kind === "teleporter") {
        this.spawn(decalKey(decal), decal.cell, "teleporter");
      } else {
        this.spawn(
          decalKey(decal),
          decal.cell,
          decal.direction === "down" ? "stairs-down" : "stairs-up",
        );
      }
    }

    this.unsubs.push(
      EventBus.on("world:trap_revealed", ({ trapId, cell }) => {
        if (!this.traps.has(trapId)) return;
        const state: FloorDecalKind = WorldStore.isTrapDisarmed(trapId)
          ? "trap-disarmed"
          : "trap-armed";
        this.spawn(`trap:${trapId}`, cell, state);
        this.makeTrapPickable(`trap:${trapId}`, trapId, cell);
      }),
    );

    this.unsubs.push(
      EventBus.on("world:trap_disarmed", ({ trapId }) => {
        this.swap(`trap:${trapId}`, "trap-disarmed");
      }),
    );

    this.unsubs.push(
      EventBus.on("world:trap_triggered", ({ trapId }) => {
        this.swap(`trap:${trapId}`, "trap-triggered");
      }),
    );
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    for (const mesh of this.meshes.values()) {
      this.scene.remove(mesh);
      mesh.geometry.dispose();
      const mat = mesh.material as THREE.Material;
      mat.dispose();
    }
    this.meshes.clear();
    this.traps.clear();
  }

  /** Test hook: which decal keys are currently in the scene. */
  getActiveKeys(): readonly string[] {
    return [...this.meshes.keys()];
  }

  private spawn(key: string, cell: Position, kind: FloorDecalKind): void {
    const existing = this.meshes.get(key);
    if (existing) {
      this.swap(key, kind);
      return;
    }
    const geo = new THREE.PlaneGeometry(DECAL_SIZE, DECAL_SIZE);
    const mat = new THREE.MeshStandardMaterial({
      map: getFloorDecalTexture(kind),
      transparent: true,
      side: THREE.DoubleSide,
      roughness: 0.9,
      metalness: 0.0,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(cell.x * CELL_SIZE, DECAL_Y, cell.z * CELL_SIZE);
    mesh.rotation.x = -Math.PI / 2;
    this.scene.add(mesh);
    this.meshes.set(key, mesh);
  }

  private swap(key: string, kind: FloorDecalKind): void {
    const mesh = this.meshes.get(key);
    if (!mesh) return;
    const mat = mesh.material as THREE.MeshStandardMaterial;
    mat.map = getFloorDecalTexture(kind);
    mat.needsUpdate = true;
  }

  /**
   * Put a revealed trap's decal on the interactive raycast layer so the
   * disarm click rule in main.tsx can hit it. State checks (disarmed,
   * already fired, adjacency) live in the rule, not here.
   */
  private makeTrapPickable(key: string, trapId: string, cell: Position): void {
    const mesh = this.meshes.get(key);
    if (!mesh) return;
    mesh.userData = { instanceId: trapId, kind: "trap", cellX: cell.x, cellZ: cell.z };
    mesh.layers.enable(INTERACTIVE_LAYER);
  }
}

function decalKey(d: FloorDecalDescriptor): string {
  return `${d.kind}:${d.id}`;
}
